import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { Tooltip } from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { useSelector } from 'react-redux';

import { useGetCostsInvoicing, usePostCostInvoicing, usePutCostInvoicing } from 'api';
import navigationPaths from 'engine/navigationPaths';
import { selectIsRegistered } from 'features/logon/logonSlice';

import LoginRegisterChoiceDialog from 'components/loginRegisterChoiceDialog';
import ToastMessage from 'components/toastMessage';
import DashboardLayout from 'components/dashboardLayout/dashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { Box, Button, Loader, Switch } from 'components/ui';
import { Attachments, Description, General, Prices } from './components';

import * as Styled from './CostForm.styles';

export const CostForm = () => {
  const { costId } = useParams();
  const navigate = useNavigate();
  const isRegistered = useSelector(selectIsRegistered);

  const [toastData, setToastData] = useState(null);
  const [isAdvancedView, setIsAdvancedView] = useState(true);
  const [isLoginModal, setIsLoginModal] = useState(false);
  const [now, setNow] = useState(new Date());

  const { data: costData, isLoading: isCostDataLoading } = useGetCostsInvoicing(costId, {
    enabled: !!costId,
  });
  const { mutate: createCost, isLoading: isCostCreating } = usePostCostInvoicing({
    onSuccess: () => {
      setToastData({ severity: 'success', message: 'Zapisano koszt' });
      navigate(navigationPaths().CostInvoices);
    },
    onError: (e) => {
      setToastData({ severity: 'error', message: 'Nie udało się zapisać kosztu' });
    },
  });
  const { mutate: updateCost, isLoading: isCostUpdating } = usePutCostInvoicing(costId, {
    onSuccess: () => {
      setToastData({ severity: 'success', message: 'Zapisano koszt' });
      navigate(navigationPaths().CostInvoices);
    },
    onError: () => {
      setToastData({ severity: 'error', message: 'Nie udało się zapisać kosztu' });
    },
  });

  const validateSchema = (values) => {
    const errors = {
      amountToPay: ' ',
      amountToPayNet: ' ',
      dateOfIssueUtc: ' ',
      dateOfSaleUtc: ' ',
      fullNumber: ' ',
      comments: ' ',
      attachments: ' ',
    };

    if (
      values.amountToPay > 0 ||
      values.amountToPayNet > 0 ||
      values.dateOfIssueUtc ||
      values.dateOfSaleUtc ||
      values.fullNumber ||
      values.comments ||
      values.attachments.length > 0
    ) {
      return true;
    }

    setToastData({
      severity: 'error',
      message: 'Przynajmniej jedna wartość musi zostać podana w celu zapisania dokumentu.',
    });
    return errors;
  };

  const formik = useFormik({
    initialValues: {
      amountToPay: get(costData, 'invoice.amountToPay', undefined),
      amountToPayNet: get(costData, 'invoice.amountToPayNet', undefined),
      attachments: get(costData, 'invoice.attachments', []),
      comments: get(costData, 'invoice.comments', ''),
      dateOfIssueUtc: get(costData, 'invoice.dateOfIssueUtc', now),
      dateOfSaleUtc: get(costData, 'invoice.dateOfSaleUtc', now),
      fullNumber: get(costData, 'invoice.fullNumber', ''),
    },
    validate: validateSchema,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (!isRegistered) {
        return setIsLoginModal(true);
      }

      costId ? updateCost(values) : createCost(values);
    },
  });

  const defaultAttachments = useMemo(() => get(costData, 'invoice.attachments', []), [costData]);

  return (
    <DashboardLayout>
      {toastData && (
        <ToastMessage
          open
          onClose={() => setToastData(null)}
          severity={toastData.severity}
          message={toastData.message}
        />
      )}

      <DashboardNavbar />

      {!!costId && isCostDataLoading ? (
        <Loader />
      ) : (
        <Styled.Wrapper>
          <Box>
            <Styled.Header>
              <span>Faktura Kosztowa</span>
              <Switch onChange={() => setIsAdvancedView(true)} checked label="Widok zaawansowany" />
            </Styled.Header>

            <Styled.Section>
              <General form={formik} isAdvancedView={isAdvancedView} />
            </Styled.Section>

            <Styled.Section>
              <Styled.Title>Załączniki</Styled.Title>
              <Attachments form={formik} defaultAttachments={defaultAttachments} />
            </Styled.Section>

            <Styled.Section>
              <Styled.Title>Informacje o kosztach</Styled.Title>
              <Description form={formik} />
            </Styled.Section>

            <Styled.Section>
              <Prices form={formik} />
            </Styled.Section>
          </Box>

          <Box>
            <Styled.Actions>
              <Button variant="outlined" onClick={() => navigate(navigationPaths().CostInvoices)}>
                Anuluj
              </Button>

              <div className="saveWrapper">
                <Tooltip title="Zapis faktury nie powoduje skutków księgowych. Faktury nie są automatycznie wysyłane do Urzędu Skarbowego i Krajowego Systemu e-Faktur (KSEF).">
                  <HelpOutline className="tooltipIcon" />
                </Tooltip>

                <Button
                  onClick={formik.handleSubmit}
                  disabled={isEqual(formik.values, formik.initialValues)}
                  loading={isCostCreating || isCostUpdating}
                >
                  Zapisz
                </Button>
              </div>
            </Styled.Actions>
          </Box>

          {isLoginModal && (
            <LoginRegisterChoiceDialog
              open
              onClose={() => setIsLoginModal(false)}
              onSuccess={formik.handleSubmit}
              fullWidth
              maxWidth="md"
            />
          )}
        </Styled.Wrapper>
      )}
    </DashboardLayout>
  );
};
